/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import { Box, CircularProgress, Tooltip, tooltipClasses } from '@mui/material';
import DeleteDialog from 'ui-component/delete-dialog';
import AppReactDropzone from 'ui-component/app-react-dropzone';
import { useTicketFileDelete, useTicketFileUpload } from 'hooks/useTickets';
import { useFileUpload } from 'hooks/useCommonHooks';
import { DragIndicator } from '@mui/icons-material';
import { useBreakpoint, truncatedText } from 'utils/commonUtil';

// Styled component for the heading inside the dropzone area
const HeadingTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(4)
  }
}));

const HtmlTooltip = styled(({ className, ...props }) => <Tooltip enterTouchDelay={0} {...props} classes={{ popper: className }} arrow />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: '100%',
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9'
    }
  })
);

const ImagePicker = ({ files, saveImage, fileListArr, setFieldValue }) => {
  const { mutate: deleteFile, isPending: isDeleteLoading } = useTicketFileDelete();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [delIndex, setDelIndex] = useState('');
  const [delFile, setDelFile] = useState({});
  const breakpoint = useBreakpoint();

  const config = {
    /*accept: {
      '*': []
    },*/
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/gif': []
    }, // Only allow image files
    // maxFiles: 1, // Limit to one file only
    multiple: true,
    maxSize: 2097152, // 2MB max file size
    // maxSize: 11097152, // 2MB max file size
    onDrop: (acceptedFiles) => saveImage(acceptedFiles),
    onDropRejected: (errors) => {
      errors.map((error) => {
        let { file, errors } = error;
        errors.map((err) =>
          toast.error(err.code === 'file-invalid-type' ? err.message : `${file.path} file Size should be less than 2MB.`, {
            autoClose: 3000
          })
        );
      });
    }
  };

  const { getRootProps, getInputProps } = useDropzone(config);

  // Render the preview for files
  const renderFilePreview = (file, dimension = 40) => {
    if (typeof file === 'string') {
      // It's a URL (for example, from initialValues)
      return <img width={dimension} height={dimension} alt="file" src={file} />;
    } else if (file?.type && file?.type.startsWith('image')) {
      // It's a new image file
      return <img width={dimension} height={dimension} alt={file.name} src={URL.createObjectURL(file)} />;
    } else if (file?.mimetype && file?.mimetype.startsWith('image')) {
      // It's an uploaded image (e.g., URL from the server)
      return <img width={dimension} height={dimension} alt={file.name} src={file?.url} />;
    } else {
      return <DescriptionIcon />;
    }
  };

  // Render file list item with drag-and-drop capabilities
  const fileList = files?.map((file, index) => {
    const truncatedLimit = breakpoint === 'xs' ? 10 : 30;
    return (
      <Draggable key={file?.name || file} draggableId={file?.name || file} index={index}>
        {(provided) => (
          <ListItem ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="file-prvw-contnt">
            <div className="file-details">
              <DragIndicator />
              <span style={{ padding: '0 5px 0 2px', fontWeight: 'bold' }}>{index + 1}.</span>
              <div className="file-preview">
                <HtmlTooltip title={renderFilePreview(file, 300)}>{renderFilePreview(file)}</HtmlTooltip>
              </div>
              <div>
                <Typography className="file-name machineAdsEllipsis" title={file?.name || file}>
                  {truncatedText(file?.name || file, truncatedLimit)}
                </Typography>
                {typeof file !== 'string' && (
                  <>
                    <Typography className="file-size" variant="body2">
                      {Math.round(file?.size / 100) / 10 > 1000
                        ? `${(Math.round(file?.size / 100) / 10000).toFixed(1)} mb`
                        : `${(Math.round(file?.size / 100) / 10).toFixed(1)} kb`}
                    </Typography>
                  </>
                )}
              </div>
            </div>
            <IconButton
              sx={{ position: 'absolute !important', right: '0 !important' }}
              onClick={() => {
                deleteOpenModal(file, index);
              }}
              color="error"
            >
              <CloseIcon />
            </IconButton>
          </ListItem>
        )}
      </Draggable>
    );
  });

  const deleteOpenModal = (file, index) => {
    // console.log('delete open modal : ', file, index);
    if (typeof file === 'string') {
      //file?.mimetype
      setDelFile(file);
      setDelIndex(index);
      setOpenDeleteModal(true);
    } else {
      removeImage(file, index);
    }
  };

  const removeImage = (file, index) => {
    let data = files.filter((_, inx) => inx !== index);
    setFieldValue('documents', data);
  };

  const removeFileFromList = () => {
    // if (delFile?.type) {
    if (typeof delFile === 'string') {
      //mimetype
      deleteFile(
        {
          url: delFile?.url
        },
        {
          onSuccess: () => {
            removeImage(delFile, delIndex);
          },
          onError: (error) => {
            console.error('Mutation failed:', error);
          }
        }
      );
    }
    setOpenDeleteModal(false);
  };

  // Handle drag-and-drop reordering
  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;

    const items = Array.from(files);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    setFieldValue('documents', items); // Update Formik values
  };

  return (
    <>
      <AppReactDropzone>
        <div {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} />
          <div className="flex items-center flex-row ">
            <img
              alt="Upload img"
              src="https://demos.themeselection.com/marketplace/materio-mui-nextjs-admin-template/documentation/images/misc/file-upload.png"
              style={{
                height: '100px',
                width: '100px',
                margin: '0 auto 15px',
                display: 'block'
              }}
            />
            <div className="flex flex-col text-center">
              <HeadingTypography variant="h5">Drop files here or click to upload.</HeadingTypography>
            </div>
          </div>
        </div>
        {files?.length ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="file-list">
              {(provided) => (
                <List ref={provided.innerRef} {...provided.droppableProps} style={{ padding: 0 }}>
                  {fileList}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        ) : null}
      </AppReactDropzone>

      <DeleteDialog
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        title={'Delete File'}
        subTitle={'Are you sure you want to delete this file?'}
        handleSubmit={() => removeFileFromList()}
        isDeleteLoading={isDeleteLoading}
      />
    </>
  );
};

const DocumentUploadMachine = ({ values, errors, touched, setFieldValue, from = 'machines' }) => {
  // const { mutate: uploadMutate, isPending: isUploadingFile } = useTicketFileUpload();
  const { mutate: uploadMutate, isPending: isUploadingFile } = useFileUpload();

  const uploadFiles = (acceptedFiles) => {
    let fileData = new FormData();

    acceptedFiles.forEach((file, i) => {
      fileData.append(`file_${i}`, file);
    });

    // fileData?.append('event', 'machines');
    fileData?.append('event', from);
    uploadMutate(fileData, {
      onSuccess: (resp) => {
        // console.log('🚀 ~ uploadFiles ~ resp:', resp);
        if (from === 'machines') {
          //From The Machine Advertisement
          const data = values?.documents?.concat(resp?.data?.data);
          setFieldValue('documents', data);
        } else if (from === 'superAdminAdvertisement') {
          //From The Super Admin Setting Advertisement
          // debugger;
          // console.log('Ads Upload : resp?.data?.data', resp?.data?.data);
          const data = values?.documents?.concat(resp?.data?.data);
          setFieldValue('documents', data);
          /*const imagePayloadArr = [];
          const imgArr = [];
          if (resp?.data?.data && Array.isArray(resp?.data?.data) && resp?.data?.data.length) {
            resp?.data?.data.map((imgObj) => {
              imagePayloadArr.push({ image: imgObj });
            });
            uploadMachineAdvertisements(imagePayloadArr || null, {
              onSuccess: (res) => {
                if (res.status === 200) {
                  if (res?.data?.data) {
                    // res?.data?.data?.map((img) => {
                    //   imgArr.push(img?.image);
                    // });
                    if (
                      res?.data?.data?.advertisements &&
                      Array.isArray(res?.data?.data?.advertisements) &&
                      res?.data?.data?.advertisements?.length
                    ) {
                      const data = values?.documents?.concat(res?.data?.data?.advertisements);
                      setFieldValue('documents', data);
                    }
                  }
                }
              },
              onError: (error) => {
                console.log('error', error);
              }
            });
          }*/
        }
      },
      onError: (error) => {
        console.error('Mutation failed:', error);
      }
    });
  };

  return (
    <Box sx={{ m: 1, position: 'relative' }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ImagePicker
              files={values.documents}
              saveImage={(acceptedFiles) => {
                // let updatedFiles = values?.documents?.concat(acceptedFiles);
                // setFieldValue('documents', updatedFiles);
                // console.log('acceptedFiles : ', acceptedFiles);
                uploadFiles(acceptedFiles);
              }}
              fileListArr={values?.documents}
              setFieldValue={setFieldValue}
            />
            <FormHelperText sx={{ marginLeft: 4 }}>
              {touched.documents && errors.documents ? <span style={{ color: 'red' }}>{errors.documents}</span> : null}
            </FormHelperText>
          </Grid>
        </Grid>
      </CardContent>
      {isUploadingFile && (
        <CircularProgress
          size={50}
          sx={{
            // color: '#16B1FF',
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px'
          }}
        />
      )}
    </Box>
  );
};

export default DocumentUploadMachine;
